import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { AppLayout } from '../components/Layout';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Building, GraduationCap, Briefcase, Award, CheckCircle2, Circle,
  Target, Brain, FileText, Users, TrendingUp, ChevronRight,
  RefreshCw, Shield, Zap, ExternalLink, Clock,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REQ_CONFIG = [
  { key: 'has_assessment',  label: 'Career Assessment',      desc: 'Complete the Holland / RIASEC career assessment', icon: Brain,       link: '/app/assessment', action: 'Take Assessment' },
  { key: 'career_goal',     label: 'Career Goal',            desc: 'Set a career direction for your FuturePath',     icon: Target,      link: '/app/career-explore', action: 'Explore Careers' },
  { key: 'cte_pathway',     label: 'CTE Pathway',            desc: 'Select a CTE cluster aligned to your goal',      icon: Award,       link: '/app/pathways', action: 'Choose Pathway' },
  { key: 'has_course_plan', label: 'Course Plan',            desc: 'Create your 4-year academic course sequence',    icon: FileText,    link: '/app/course-plan', action: 'Build Plan' },
  { key: 'wbl_done',        label: 'Work-Based Learning',    desc: 'Log required WBL hours',                         icon: Briefcase,   link: '/app/wbl', action: 'Log Hours' },
  { key: 'cdp_progress',    label: 'CDP Progress',           desc: 'Complete Career Development Plan sections',      icon: TrendingUp,  link: '/app/my-cdp', action: 'Open CDP' },
  { key: 'parent_acknowledged', label: 'Parent/Guardian Acknowledgment', desc: 'Parent reviews and acknowledges your plan', icon: Users, link: '#', action: 'Share with Parent' },
];

export default function MySchoolPlanPage() {
  const { getAuthHeader } = useAuth();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandSection, setExpandSection] = useState(null);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/nc/school-plan-summary`, getAuthHeader())
      .then(r => setPlan(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  if (loading) return (
    <AppLayout>
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-cyan-400 border-t-transparent" />
      </div>
    </AppLayout>
  );

  if (!plan?.is_institutional) return (
    <AppLayout>
      <div className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-10 text-center max-w-lg mx-auto mt-10">
        <Building className="h-10 w-10 text-slate-300 mx-auto mb-3" />
        <p className="text-sm text-slate-300">District plan information is not configured for your account.</p>
      </div>
    </AppLayout>
  );

  const done = [
    plan.has_assessment,
    !!plan.career_goal,
    !!plan.cte_pathway,
    plan.has_course_plan,
    (plan.wbl_hours || 0) >= (plan.wbl_goal || 40),
    (plan.sections_complete || 0) >= 2,
    plan.parent_acknowledged,
  ];
  const doneCount = done.filter(Boolean).length;
  const pct = Math.round((doneCount / done.length) * 100);

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto space-y-6 pb-12" data-testid="my-school-plan-page">

        {/* ── Hero header ─────────────────────────────────────────────────── */}
        <div className="relative overflow-hidden rounded-2xl border border-cyan-500/20 bg-[#0a1729] px-7 py-8">
          <div aria-hidden className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(34,211,238,0.12), transparent 65%)' }} />

          {/* Identity: school / grade / counselor */}
          <div className="flex flex-wrap items-center gap-6 mb-6">
            {plan.school_name && (
              <div className="flex items-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-cyan-500/25 bg-cyan-500/[0.08]">
                  <Building className="h-4 w-4 text-cyan-400" />
                </div>
                <div>
                  <p className="text-[9.5px] font-bold uppercase tracking-widest text-cyan-400/70">School</p>
                  <p className="text-sm font-bold text-white">{plan.school_name}</p>
                </div>
              </div>
            )}
            {plan.grade && (
              <div className="flex items-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-violet-500/25 bg-violet-500/[0.08]">
                  <GraduationCap className="h-4 w-4 text-violet-400" />
                </div>
                <div>
                  <p className="text-[9.5px] font-bold uppercase tracking-widest text-violet-400/70">Grade</p>
                  <p className="text-sm font-bold text-white">{plan.grade_display || `Grade ${plan.grade}`}</p>
                </div>
              </div>
            )}
            {plan.counselor_name && (
              <div className="flex items-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-emerald-500/25 bg-emerald-500/[0.08]">
                  <Users className="h-4 w-4 text-emerald-400" />
                </div>
                <div>
                  <p className="text-[9.5px] font-bold uppercase tracking-widest text-emerald-400/70">Counselor</p>
                  <p className="text-sm font-bold text-white">{plan.counselor_name}</p>
                </div>
              </div>
            )}
          </div>

          {/* Progress */}
          <div className="flex items-end justify-between mb-2">
            <div>
              <p className="text-[10.5px] font-bold uppercase tracking-[0.15em] text-cyan-400/80 mb-0.5">
                {plan.district || 'District'} · Career Development Plan
              </p>
              <h1 className="text-2xl font-black text-white">Your School Plan</h1>
            </div>
            <div className="text-right">
              <p className={`text-4xl font-black tabular-nums ${pct === 100 ? 'text-emerald-400' : 'text-cyan-300'}`}>{pct}%</p>
              <p className="text-[11px] text-slate-300">{doneCount}/{done.length} complete</p>
            </div>
          </div>
          <div className="h-2 w-full rounded-full bg-white/[0.05] overflow-hidden">
            <motion.div
              initial={{ width: 0 }} animate={{ width: `${pct}%` }}
              transition={{ duration: 0.9, ease: 'easeOut' }}
              className="h-2 rounded-full"
              style={{ background: pct === 100 ? '#34d399' : 'linear-gradient(90deg, #22d3ee, #818cf8)' }} />
          </div>
        </div>

        {/* ── Requirements list ─────────────────────────────────────────── */}
        <div className="rounded-2xl border border-white/[0.07] bg-[#0b1b30] overflow-hidden">
          <div className="px-5 py-4 border-b border-white/[0.05] flex items-center justify-between">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-300">Requirements</p>
            <button onClick={load}
              className="flex items-center gap-1.5 text-[11px] text-slate-300 hover:text-slate-300 transition-colors">
              <RefreshCw className="h-3 w-3" /> Refresh
            </button>
          </div>
          <div className="divide-y divide-white/[0.04]">
            {REQ_CONFIG.map((cfg, i) => {
              const isDone = done[i];
              const val = i === 4
                ? (plan.wbl_hours || 0) >= (plan.wbl_goal || 40)
                : i === 5
                ? (plan.sections_complete || 0) >= 2
                : plan[cfg.key];
              const open = expandSection === cfg.key;

              // Specific detail per requirement
              const detail =
                cfg.key === 'career_goal'     ? plan.career_goal :
                cfg.key === 'cte_pathway'     ? plan.cte_pathway :
                cfg.key === 'wbl_done'        ? `${plan.wbl_hours || 0}h of ${plan.wbl_goal || 40}h` :
                cfg.key === 'cdp_progress'    ? `${plan.sections_complete || 0}/${plan.total_sections || 9} sections` :
                null;

              return (
                <div key={cfg.key}>
                  <button
                    onClick={() => setExpandSection(open ? null : cfg.key)}
                    className="w-full flex items-center gap-4 px-5 py-4 hover:bg-white/[0.02] transition-colors text-left group">
                    {/* Icon */}
                    <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border ${
                      isDone
                        ? 'border-emerald-500/30 bg-emerald-500/[0.07]'
                        : 'border-slate-600/40 bg-white/[0.025]'
                    }`}>
                      <cfg.icon className={`h-4 w-4 ${isDone ? 'text-emerald-400' : 'text-slate-300'}`} />
                    </div>

                    {/* Label + detail */}
                    <div className="flex-1 min-w-0">
                      <p className={`text-[13px] font-semibold ${isDone ? 'text-white' : 'text-slate-300'}`}>
                        {cfg.label}
                      </p>
                      {detail && <p className="text-[11px] text-cyan-300/80 mt-0.5">{detail}</p>}
                    </div>

                    {/* Status chip */}
                    <span className={`shrink-0 text-[10.5px] px-2.5 py-1 rounded-full border font-semibold ${
                      isDone
                        ? 'bg-emerald-500/[0.08] border-emerald-500/20 text-emerald-400'
                        : 'bg-amber-500/[0.08] border-amber-500/20 text-amber-400'
                    }`}>
                      {isDone ? 'Complete' : 'Needed'}
                    </span>

                    {/* Check / circle */}
                    <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
                      isDone ? 'bg-emerald-500/[0.12]' : 'bg-white/[0.03]'
                    }`}>
                      {isDone
                        ? <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                        : <Circle className="h-4 w-4 text-slate-300" />}
                    </div>
                  </button>

                  {/* Expandable detail */}
                  <AnimatePresence>
                    {open && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden">
                        <div className="px-5 pb-4 ml-13 pl-[3.25rem] border-t border-white/[0.04]">
                          <p className="text-[12px] text-slate-300 pt-3 mb-3">{cfg.desc}</p>
                          {!isDone && (
                            <Link to={cfg.link}
                              className="inline-flex items-center gap-1.5 rounded-lg border border-cyan-400/25
                                bg-cyan-400/[0.06] px-3.5 py-2 text-[11.5px] font-semibold text-cyan-300
                                hover:bg-cyan-400/[0.10] transition-all">
                              {cfg.action} <ChevronRight className="h-3.5 w-3.5" />
                            </Link>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── WBL Breakdown ───────────────────────────────────────────────── */}
        <div className="rounded-2xl border border-white/[0.07] bg-[#0b1b30] p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-amber-400" />
              <p className="text-sm font-semibold text-white">Work-Based Learning</p>
            </div>
            <span className={`text-[11.5px] px-2.5 py-1 rounded-full font-semibold border ${
              (plan.wbl_hours || 0) >= (plan.wbl_goal || 40)
                ? 'bg-emerald-500/[0.08] border-emerald-500/20 text-emerald-400'
                : 'bg-amber-500/[0.08] border-amber-500/20 text-amber-400'
            }`}>{plan.wbl_hours || 0}h / {plan.wbl_goal || 40}h</span>
          </div>

          <div className="h-2 w-full rounded-full bg-white/[0.05] overflow-hidden mb-4">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${Math.min(100, ((plan.wbl_hours || 0) / (plan.wbl_goal || 40)) * 100)}%` }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              className="h-2 rounded-full"
              style={{ background: (plan.wbl_hours || 0) >= (plan.wbl_goal || 40) ? '#34d399' : '#f59e0b' }} />
          </div>

          {(plan.wbl_experiences || []).length > 0 ? (
            <div className="space-y-2">
              {plan.wbl_experiences.map((exp, i) => (
                <div key={i}
                  className="flex items-center justify-between rounded-lg border border-white/[0.05]
                    bg-white/[0.02] px-4 py-2.5">
                  <div className="min-w-0">
                    <p className="text-[12.5px] font-medium text-slate-300 truncate">{exp.employer}</p>
                    <p className="text-[11px] text-slate-300">{exp.type}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-3">
                    <span className="text-[12px] font-bold text-white">{exp.hours}h</span>
                    {exp.verified && (
                      <span className="flex items-center gap-1 text-[10.5px] text-emerald-400">
                        <CheckCircle2 className="h-3 w-3" /> Verified
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[12px] text-slate-300 text-center py-2">No WBL experiences logged yet.</p>
          )}
        </div>

        {/* ── NC Framework note ─────────────────────────────────────────── */}
        <div className="flex items-start gap-3 rounded-xl border border-white/[0.05] bg-white/[0.015] px-5 py-4">
          <Shield className="h-4 w-4 text-slate-300 shrink-0 mt-0.5" />
          <div>
            <p className="text-[12px] font-semibold text-slate-300">North Carolina Career Development Plan</p>
            <p className="text-[11px] text-slate-300 mt-0.5">
              Requirements align with the NCDPI Career Development Plan framework.
              Completing all requirements supports your graduation readiness and postsecondary pathway.
            </p>
          </div>
        </div>

      </div>
    </AppLayout>
  );
}
